import React from "react";
import { Link } from "react-router-dom";
import { Eye, ShoppingBag } from "lucide-react";
import { formatINR } from "@/lib/format";
import { StarRating } from "@/components/common/StarRating";
import { WishlistButton } from "@/components/product/WishlistButton";
import { ProductBadges } from "@/components/product/ProductBadges";
import { useCart } from "@/context/CartContext";

function useQuickAdd(product, onQuickView) {
  const { addItem } = useCart();
  const soldOut = !product.available;
  const multiVariant = product.variants?.length > 1;
  const label = soldOut ? "Sold Out" : multiVariant ? "Select Options" : "Quick Add";
  const quickAdd = (e) => {
    e.preventDefault();
    if (soldOut) return;
    if (multiVariant && onQuickView) return onQuickView(product);
    addItem(product, product.variants?.[0]?.id, 1);
  };
  return { soldOut, label, quickAdd };
}

function ProductCardMedia({ product, onQuickView }) {
  const primary = product.images?.[0];
  const secondary = product.images?.[1] || primary;
  const { soldOut, label, quickAdd } = useQuickAdd(product, onQuickView);
  return (
    <div className="relative aspect-[4/5] overflow-hidden bg-cream">
      <img
        src={primary}
        alt={product.title}
        loading="lazy"
        data-testid="product-card-image"
        className="absolute inset-0 h-full w-full object-cover object-center transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-0"
      />
      <img
        src={secondary}
        alt={`${product.title} alternate view`}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover object-center opacity-0 transition-all duration-700 ease-out group-hover:opacity-100 group-hover:scale-105"
      />
      <ProductBadges product={product} />
      <div className="absolute right-3 top-3 flex flex-col gap-2">
        <WishlistButton product={product} className="h-9 w-9" />
        {onQuickView && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onQuickView(product); }}
            aria-label="Quick view"
            data-testid="product-card-quick-view"
            className="grid h-9 w-9 place-items-center rounded-full bg-white/85 backdrop-blur-sm text-ink shadow-sm opacity-0 -translate-y-1 transition-all duration-300 hover:scale-110 hover:text-gold group-hover:opacity-100 group-hover:translate-y-0"
          >
            <Eye size={18} />
          </button>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 translate-y-full opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
        <button
          type="button"
          onClick={quickAdd}
          disabled={soldOut}
          data-testid="product-card-quick-add"
          className="flex w-full items-center justify-center gap-2 bg-ink py-3.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-gold disabled:cursor-not-allowed disabled:bg-muted-foreground/60"
        >
          <ShoppingBag size={14} /> {label}
        </button>
      </div>
    </div>
  );
}

function ProductCardInfo({ product }) {
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  return (
    <div className="pt-4 text-center sm:text-left">
      <p className="text-luxe text-[10px] text-gold mb-1.5">{product.category}</p>
      <Link to={`/product/${product.handle}`}>
        <h3 className="font-serif text-lg sm:text-xl text-ink leading-snug hover:text-gold transition-colors">{product.title}</h3>
      </Link>
      <div className="mt-1.5 flex items-center justify-center sm:justify-start gap-2">
        <StarRating rating={product.rating} size={12} />
        <span className="text-xs text-muted-foreground">({product.review_count})</span>
      </div>
      <div className="mt-2 flex items-baseline justify-center sm:justify-start gap-2">
        <span className="font-sans text-sm font-semibold text-ink">{formatINR(product.price)}</span>
        {onSale && <span className="text-sm text-muted-foreground line-through">{formatINR(product.compare_at_price)}</span>}
      </div>
    </div>
  );
}

export function ProductCard({ product, onQuickView }) {
  return (
    <div className="group relative flex flex-col" data-testid="product-card">
      <Link to={`/product/${product.handle}`} className="block">
        <ProductCardMedia product={product} onQuickView={onQuickView} />
      </Link>
      <ProductCardInfo product={product} />
    </div>
  );
}
